import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Data from '../../repository';

const sidebarItems = [
	{ key: 'dashboard', label: 'Dashboard', path: '/super-admin/dashboard' },
	{ key: 'customers', label: 'Customers', path: '/super-admin/customers' },
	{ key: 'domains', label: 'Domains', path: '/super-admin/domains' }
];

const cardStyle = {
	background: '#fff',
	border: '1px solid #e9ecef',
	borderRadius: '8px'
};

const thStyle = {
	textAlign: 'left',
	padding: '10px 12px',
	fontWeight: 600,
	fontSize: '14px'
};

const thStyleAction = {
	textAlign: 'center',
	padding: '10px 12px',
	fontWeight: 600,
	fontSize: '14px'
};

const tdStyle = {
	padding: '10px 12px',
	fontSize: '14px'
};

const formatDate = (value) => {
	if (!value) return '-';
	try {
		return new Date(value).toLocaleString();
	} catch (_err) {
		return value;
	}
};

const buildCustomerCopyText = (customer) => {
	return [
		`Company Name: ${customer.companyName || '-'}`,
		`First Name: ${customer.contactFirstName || '-'}`,
		`Last Name: ${customer.contactLastName || '-'}`,
		`Email: ${customer.contactEmail || '-'}`,
		`From Email: ${customer.fromEmail || '-'}`,
		`Phone: ${customer.contactPhone || '-'}`,
		`Business Type: ${customer.businessType || '-'}`,
		customer.businessTypeOther ? `Business Type (Other): ${customer.businessTypeOther}` : null,
		`Business Details: ${customer.businessDetails || '-'}`,
		`Preferred Domain: ${customer.preferredDomain || '-'}`,
		`Website: ${customer.website || '-'}`,
		`Auto Accept Booking: ${customer.autoAcceptBooking ? 'Yes' : 'No'}`,
		`Status: ${customer.status || '-'}`,
		`Submitted At: ${formatDate(customer.createdAt)}`,
		`Notes: ${customer.notes || '-'}`
	].filter(Boolean).join('\n');
};

function SuperAdminPortal({ page }) {
	const navigate = useNavigate();
	const { id } = useParams();

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [search, setSearch] = useState('');
	const [domains, setDomains] = useState([]);
	const [domainDetail, setDomainDetail] = useState(null);
	const [customers, setCustomers] = useState([]);
	const [customerDetail, setCustomerDetail] = useState(null);
	const [updatingCustomerId, setUpdatingCustomerId] = useState(null);
	const [copiedCustomerId, setCopiedCustomerId] = useState(null);
	const [togglingDomainId, setTogglingDomainId] = useState(null);
	const [editingDomain, setEditingDomain] = useState(false);
	const [domainForm, setDomainForm] = useState({ name: '', heading: '', adminEmail: '', fromEmail: '', base: '' });
	const [savingDomain, setSavingDomain] = useState(false);
	const [showAddAdmin, setShowAddAdmin] = useState(false);
	const [addAdminForm, setAddAdminForm] = useState({ firstName: '', lastName: '', email: '', phoneNumber: '' });
	const [addingAdmin, setAddingAdmin] = useState(false);
	const [addAdminError, setAddAdminError] = useState(null);
	const [addAdminResult, setAddAdminResult] = useState(null);

	useEffect(() => {
		const load = async () => {
			if (!['domains', 'domain-view', 'domain-users', 'customers', 'customer-view'].includes(page)) return;

			setLoading(true);
			setError(null);
			try {
				if (page === 'domains') {
					const response = await Data.fetchSuperAdminDomains();
					setDomains(response.data?.domains || []);
					setDomainDetail(null);
					setCustomers([]);
					setCustomerDetail(null);
				} else if (page === 'domain-view' && id) {
					const response = await Data.fetchSuperAdminDomainUsers(id);
					setDomainDetail(response.data || null);
					setDomains([]);
					setCustomers([]);
					setCustomerDetail(null);
				} else if (page === 'domain-users' && id) {
					const response = await Data.fetchSuperAdminDomainUsers(id);
					setDomainDetail(response.data || null);
					setCustomers([]);
					setCustomerDetail(null);
				} else if (page === 'customers') {
					const response = await Data.fetchSuperAdminCustomers();
					setCustomers(response.data?.customers || []);
					setDomains([]);
					setDomainDetail(null);
					setCustomerDetail(null);
				} else if (page === 'customer-view' && id) {
					const response = await Data.fetchSuperAdminCustomerById(id);
					setCustomerDetail(response.data || null);
					setCustomers([]);
					setDomains([]);
					setDomainDetail(null);
				}
			} catch (err) {
				setError(err.response?.data?.message || 'Failed to load data.');
			} finally {
				setLoading(false);
			}
		};

		load();
	}, [page, id]);

	useEffect(() => {
		setSearch('');
		setCopiedCustomerId(null);
		setEditingDomain(false);
		setShowAddAdmin(false);
		setAddAdminForm({ firstName: '', lastName: '', email: '', phoneNumber: '' });
		setAddAdminError(null);
		setAddAdminResult(null);
	}, [page, id]);

	useEffect(() => {
		if (page !== 'domain-view' || !domainDetail) return;
		setDomainForm({
			name: domainDetail.name || '',
			heading: domainDetail.heading || '',
			adminEmail: domainDetail.adminEmail || '',
			fromEmail: domainDetail.fromEmail || '',
			base: domainDetail.base || ''
		});
	}, [page, domainDetail]);

	const filteredDomains = useMemo(() => {
		if (!search) return domains;
		const query = search.toLowerCase();
		return domains.filter((domain) => (
			domain.name?.toLowerCase().includes(query) ||
			domain.businessType?.toLowerCase().includes(query) ||
			domain.adminEmail?.toLowerCase().includes(query)
		));
	}, [domains, search]);

	const filteredUsers = useMemo(() => {
		const users = domainDetail?.users || [];
		if (!search) return users;
		const query = search.toLowerCase();
		return users.filter((user) => {
			const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
			return (
				fullName.includes(query) ||
				user.email?.toLowerCase().includes(query)
			);
		});
	}, [domainDetail, search]);

	const filteredCustomers = useMemo(() => {
		if (!search) return customers;
		const query = search.toLowerCase();
		return customers.filter((customer) => {
			const fullName = `${customer.contactFirstName || ''} ${customer.contactLastName || ''}`.toLowerCase();
			return (
				customer.companyName?.toLowerCase().includes(query) ||
				fullName.includes(query) ||
				customer.contactEmail?.toLowerCase().includes(query) ||
				customer.businessType?.toLowerCase().includes(query) ||
				customer.status?.toLowerCase().includes(query)
			);
		});
	}, [customers, search]);

	const logout = () => {
		localStorage.removeItem('token');
		window.location.href = '/super-admin/login';
	};

	const updateCustomerStatus = async (customerId, status) => {
		setUpdatingCustomerId(customerId);
		try {
			const response = await Data.updateSuperAdminCustomerStatus(customerId, status);
			setCustomers((prev) => prev.map((item) => (item._id === customerId ? response.data : item)));
			setCustomerDetail((prev) => (prev?._id === customerId ? response.data : prev));
		} catch (err) {
			setError(err.response?.data?.message || 'Unable to update customer status.');
		} finally {
			setUpdatingCustomerId(null);
		}
	};

	const deleteCustomer = async (customerId) => {
		if (!window.confirm("Delete this customer onboarding request?")) return;
		setUpdatingCustomerId(customerId);
		try {
			await Data.deleteSuperAdminCustomer(customerId);
			setCustomers((prev) => prev.filter((item) => item._id !== customerId));
			setCustomerDetail((prev) => (prev?._id === customerId ? null : prev));
			navigate('/super-admin/customers');
		} catch (err) {
			setError(err.response?.data?.message || 'Unable to delete customer request.');
		} finally {
			setUpdatingCustomerId(null);
		}
	};

	const toggleDomainDisabled = async (domain) => {
		const action = domain.disabled ? 'enable' : 'disable';
		if (!window.confirm(`${action === 'disable' ? 'Disable' : 'Enable'} domain "${domain.name}"? ${action === 'disable' ? 'Users will not be able to log in.' : 'Users will be able to log in again.'}`)) return;
		setTogglingDomainId(domain._id);
		try {
			const response = await Data.updateSuperAdminDomainDisabled(domain._id, !domain.disabled);
			setDomains((prev) => prev.map((d) => d._id === domain._id ? { ...d, disabled: response.data.disabled } : d));
			setDomainDetail((prev) => (prev?._id === domain._id ? { ...prev, disabled: response.data.disabled } : prev));
		} catch (err) {
			setError(err.response?.data?.message || 'Failed to update domain status.');
		} finally {
			setTogglingDomainId(null);
		}
	};

	const togglePaidExtrasAvailable = async (domain) => {
		const action = domain.paidExtrasAvailable ? 'revoke' : 'make available';
		if (!window.confirm(`${domain.paidExtrasAvailable ? 'Revoke' : 'Make available'} Paid Extras for "${domain.name}"? ${domain.paidExtrasAvailable ? 'Their admin will no longer be able to offer it to new customers (existing purchases are unaffected).' : 'Their admin will be able to configure a price and sell it to their customers.'}`)) return;
		setTogglingDomainId(domain._id);
		try {
			const response = await Data.updateSuperAdminDomainPaidExtrasAvailable(domain._id, !domain.paidExtrasAvailable);
			setDomains((prev) => prev.map((d) => d._id === domain._id ? { ...d, paidExtrasAvailable: response.data.paidExtrasAvailable } : d));
			setDomainDetail((prev) => (prev?._id === domain._id ? { ...prev, paidExtrasAvailable: response.data.paidExtrasAvailable } : prev));
		} catch (err) {
			setError(err.response?.data?.message || 'Failed to update Paid Extras availability.');
		} finally {
			setTogglingDomainId(null);
		}
	};

	const onDomainFormChange = (field, value) => {
		setDomainForm((prev) => ({ ...prev, [field]: value }));
	};

	const saveDomain = async () => {
		if (!domainDetail) return;
		setSavingDomain(true);
		setError(null);
		try {
			const response = await Data.updateSuperAdminDomain(domainDetail._id, domainForm);
			setDomainDetail((prev) => ({ ...prev, ...response.data }));
			setEditingDomain(false);
		} catch (err) {
			setError(err.response?.data?.message || 'Unable to update domain.');
		} finally {
			setSavingDomain(false);
		}
	};

	const onAddAdminFormChange = (field, value) => {
		setAddAdminForm((prev) => ({ ...prev, [field]: value }));
	};

	const addDomainAdmin = async () => {
		if (!domainDetail) return;
		setAddingAdmin(true);
		setAddAdminError(null);
		try {
			const response = await Data.createSuperAdminDomainUser(domainDetail._id, addAdminForm);
			setDomainDetail((prev) => (prev ? {
				...prev,
				users: [response.data, ...(prev.users || [])],
				userCount: (prev.userCount || 0) + 1
			} : prev));
			setAddAdminResult(response.data);
			setAddAdminForm({ firstName: '', lastName: '', email: '', phoneNumber: '' });
		} catch (err) {
			setAddAdminError(err.response?.data?.message || 'Unable to add admin.');
		} finally {
			setAddingAdmin(false);
		}
	};

	const copyCustomerDetails = async (customer) => {
		const content = buildCustomerCopyText(customer);
		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(content);
			} else {
				const textarea = document.createElement('textarea');
				textarea.value = content;
				textarea.style.position = 'fixed';
				textarea.style.left = '-9999px';
				document.body.appendChild(textarea);
				textarea.focus();
				textarea.select();
				document.execCommand('copy');
				document.body.removeChild(textarea);
			}
			setCopiedCustomerId(customer._id);
			setTimeout(() => {
				setCopiedCustomerId((prev) => (prev === customer._id ? null : prev));
			}, 2000);
		} catch (_err) {
			setError('Unable to copy details.');
		}
	};

	const renderSidebar = () => (
		<aside
			style={{
				width: '240px',
				flexShrink: 0,
				position: 'sticky',
				top: 0,
				height: '100vh',
				background: '#0f172a',
				color: '#fff',
				padding: '20px 14px',
				display: 'grid',
				gridTemplateRows: '1fr auto'
			}}
		>
			<div>
				<p style={{ margin: 0, fontSize: '12px', letterSpacing: '0.08em', opacity: 0.65, textTransform: 'uppercase', color: '#fff' }}>Super Admin</p>
				<h2 style={{ margin: '10px 0 20px', fontSize: '20px', color: '#fff' }}>Portal</h2>
				<div style={{ display: 'grid', gap: '8px' }}>
					{sidebarItems.map((item) => {
						let active = page === item.key;
						if (item.key === 'domains') active = page === 'domains' || page === 'domain-view' || page === 'domain-users';
						if (item.key === 'customers') active = page === 'customers' || page === 'customer-view';
						return (
							<button
								key={item.key}
								type="button"
								onClick={() => navigate(item.path)}
								style={{
									border: '1px solid',
									borderColor: active ? '#60a5fa' : 'rgba(255,255,255,0.2)',
									background: active ? 'rgba(96,165,250,0.18)' : 'transparent',
									color: '#fff',
									textAlign: 'left',
									padding: '10px 12px',
									borderRadius: '6px',
									cursor: 'pointer'
								}}
							>
								{item.label}
							</button>
						);
					})}
				</div>
			</div>

				<button
					type="button"
					onClick={logout}
					style={{
						width: '100%',
						border: '1px solid #f87171',
						background: 'transparent',
						color: '#f57b7b',
						borderRadius: '6px',
						padding: '10px 12px',
						cursor: 'pointer'
					}}
				>
					Logout
				</button>
		</aside>
	);

	const renderDashboard = () => (
		<div style={{ ...cardStyle, padding: '18px 20px' }}>
			<h3 style={{ margin: 0, fontSize: '18px' }}>Dashboard</h3>
			<p style={{ margin: '10px 0 0', color: '#6b7280' }}>No widgets configured yet.</p>
		</div>
	);

	const renderCustomers = () => (
		<>
			<div style={{ ...cardStyle, padding: '14px 16px' }}>
				<input
					type="text"
					placeholder="Search customers..."
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					style={{ width: '100%' }}
				/>
			</div>

			<div style={{ ...cardStyle, overflow: 'hidden' }}>
				<div style={{ overflowX: 'auto' }}>
					<table style={{ width: '100%', borderCollapse: 'collapse' }}>
						<thead>
							<tr style={{ background: '#f8fafc' }}>
								<th style={thStyle}>Company</th>
								<th style={thStyle}>Contact</th>
								<th style={thStyle}>Email</th>
								<th style={thStyle}>From Email</th>
								<th style={thStyle}>Type</th>
								<th style={thStyle}>Auto Accept</th>
								<th style={thStyle}>Status</th>
								<th style={thStyle}>Submitted</th>
								<th style={thStyleAction}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{filteredCustomers.map((customer) => (
								<tr key={customer._id} style={{ borderTop: '1px solid #eef0f2' }}>
									<td style={tdStyle}>{customer.companyName || '-'}</td>
									<td style={tdStyle}>{`${customer.contactFirstName || ''} ${customer.contactLastName || ''}`.trim() || '-'}</td>
									<td style={tdStyle}>{customer.contactEmail || '-'}</td>
									<td style={tdStyle}>{customer.fromEmail || '-'}</td>
									<td style={tdStyle}>{customer.businessType || '-'}</td>
									<td style={tdStyle}>{customer.autoAcceptBooking ? 'Yes' : 'No'}</td>
									<td style={tdStyle}>
										<span style={{ textTransform: 'capitalize' }}>{customer.status || 'pending'}</span>
									</td>
									<td style={tdStyle}>{formatDate(customer.createdAt)}</td>
									<td style={tdStyle}>
										<button
											type="button"
											onClick={() => navigate(`/super-admin/customers/${customer._id}`)}
											style={{ border: '1px solid', background: 'transparent' }}
										>
											View
										</button>
									</td>
								</tr>
							))}
							{!filteredCustomers.length ? (
								<tr>
									<td colSpan={9} style={{ ...tdStyle, color: '#6b7280' }}>
										No customer onboarding requests found.
									</td>
								</tr>
							) : null}
						</tbody>
					</table>
				</div>
			</div>
		</>
	);

	const renderCustomerView = () => {
		const customer = customerDetail;
		if (!customer) {
			return (
				<div style={{ ...cardStyle, padding: '18px 20px' }}>
					<p style={{ margin: 0, color: '#6b7280' }}>Customer request not found.</p>
				</div>
			);
		}

		return (
			<>
				<div style={{ ...cardStyle, padding: '14px 16px', display: 'grid', gap: '12px' }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
						<div>
							<h3 style={{ margin: 0, fontSize: '18px' }}>{customer.companyName || 'Customer Request'}</h3>
							<p style={{ margin: '6px 0 0', color: '#6b7280' }}>Customer onboarding request details.</p>
						</div>
						<button type="button" onClick={() => navigate('/super-admin/customers')} style={{ border: '1px solid'}}>
							Back to Customers
						</button>
					</div>

					<div style={{ display: 'grid', gap: '10px', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
						<div><strong>Contact:</strong> {`${customer.contactFirstName || ''} ${customer.contactLastName || ''}`.trim() || '-'}</div>
						<div><strong>Email:</strong> {customer.contactEmail || '-'}</div>
						<div><strong>From Email:</strong> {customer.fromEmail || '-'}</div>
						<div><strong>Phone:</strong> {customer.contactPhone || '-'}</div>
						<div><strong>Business Type:</strong> {customer.businessType || '-'}</div>
						{customer.businessTypeOther ? <div><strong>Business Type (Other):</strong> {customer.businessTypeOther}</div> : null}
						<div><strong>Preferred Domain:</strong> {customer.preferredDomain || '-'}</div>
						<div><strong>Website:</strong> {customer.website || '-'}</div>
						<div><strong>Auto Accept Booking:</strong> {customer.autoAcceptBooking ? 'Yes' : 'No'}</div>
						<div><strong>Status:</strong> <span style={{ textTransform: 'capitalize' }}>{customer.status || 'pending'}</span></div>
						<div><strong>Submitted At:</strong> {formatDate(customer.createdAt)}</div>
					</div>

					<div style={{ gridColumn: '1 / -1' }}>
						<strong>Business Details:</strong>
						<p style={{ margin: '6px 0 0', color: '#374151' }}>{customer.businessDetails || '-'}</p>
					</div>
					<div style={{ gridColumn: '1 / -1' }}>
						<strong>Notes:</strong>
						<p style={{ margin: '6px 0 0', color: '#374151' }}>{customer.notes || '-'}</p>
					</div>
				</div>

				<div style={{ ...cardStyle, padding: '14px 16px' }}>
					<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
						<button
							type="button"
							onClick={() => updateCustomerStatus(customer._id, 'approved')}
							disabled={updatingCustomerId === customer._id || customer.status === 'approved'}
							style={{ border: '1px solid', background: 'transparent' }}
						>
							{updatingCustomerId === customer._id ? 'Saving...' : 'Approve'}
						</button>
						<button
							type="button"
							onClick={() => updateCustomerStatus(customer._id, 'rejected')}
							disabled={updatingCustomerId === customer._id || customer.status === 'rejected'}
							style={{ border: '1px solid #ef4444', color: '#dc2626', background: 'transparent' }}
						>
							{updatingCustomerId === customer._id ? 'Saving...' : 'Reject'}
						</button>
						<button
							type="button"
							onClick={() => copyCustomerDetails(customer)}
							style={{ border: '1px solid', background: 'transparent' }}
						>
							{copiedCustomerId === customer._id ? 'Copied' : 'Copy'}
						</button>
						<button
							type="button"
							onClick={() => deleteCustomer(customer._id)}
							disabled={updatingCustomerId === customer._id}
							style={{ border: '1px solid #b91c1c', color: '#b91c1c', background: 'transparent' }}
						>
							{updatingCustomerId === customer._id ? 'Deleting...' : 'Delete'}
						</button>
					</div>
				</div>
			</>
		);
	};

	const renderDomains = () => (
		<>
			<div style={{ ...cardStyle, padding: '14px 16px' }}>
				<input
					type="text"
					placeholder="Search domains..."
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					style={{ width: '100%' }}
				/>
			</div>

			<div style={{ ...cardStyle, overflow: 'hidden' }}>
				<div style={{ overflowX: 'auto' }}>
					<table style={{ width: '100%', borderCollapse: 'collapse' }}>
						<thead>
							<tr style={{ background: '#f8fafc' }}>
								<th style={thStyle}>Domain</th>
								<th style={thStyle}>Type</th>
								<th style={thStyle}>Admin Email</th>
								<th style={thStyle}>Users</th>
								<th style={thStyle}>Status</th>
								<th style={thStyleAction}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{filteredDomains.map((domain) => (
								<tr key={domain._id} style={{ borderTop: '1px solid #eef0f2' }}>
									<td style={tdStyle}>{domain.name || '-'}</td>
									<td style={tdStyle}>{domain.businessType || 'sessions'}</td>
									<td style={tdStyle}>{domain.adminEmail || '-'}</td>
									<td style={tdStyle}>{domain.userCount || 0}</td>
									<td style={tdStyle}>
										<span style={{ color: domain.disabled ? '#b91c1c' : '#15803d', fontWeight: 600 }}>
											{domain.disabled ? 'Disabled' : 'Active'}
										</span>
									</td>
									<td style={tdStyle}>
										<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
											<button
												type="button"
												onClick={() => navigate(`/super-admin/domains/${domain._id}`)}
												style={{ border: '1px solid', background: 'transparent' }}
											>
												View
											</button>
											<button
												type="button"
												onClick={() => navigate(`/super-admin/domains/${domain._id}/users`)}
												style={{ border: '1px solid', background: 'transparent' }}
											>
												Users
											</button>
											{!domain.disabled && domain.directUrl ? (
												<a
													href={domain.directUrl}
													target="_blank"
													rel="noreferrer"
													className="btn btnPrimary"
													style={{ textDecoration: 'none' }}
												>
													Login
												</a>
											) : null}
											<button
												type="button"
												onClick={() => toggleDomainDisabled(domain)}
												disabled={togglingDomainId === domain._id}
												style={{
													border: `1px solid ${domain.disabled ? '#15803d' : '#b91c1c'}`,
													color: domain.disabled ? '#15803d' : '#b91c1c',
													background: 'transparent',
													cursor: 'pointer'
												}}
											>
												{togglingDomainId === domain._id ? '...' : domain.disabled ? 'Enable' : 'Disable'}
											</button>
										</div>
									</td>
								</tr>
							))}
							{!filteredDomains.length ? (
								<tr>
									<td colSpan={6} style={{ ...tdStyle, color: '#6b7280' }}>
										No domains found.
									</td>
								</tr>
							) : null}
						</tbody>
					</table>
				</div>
			</div>
		</>
	);

	const renderDomainView = () => {
		const domain = domainDetail;
		if (!domain) {
			return (
				<div style={{ ...cardStyle, padding: '18px 20px' }}>
					<p style={{ margin: 0, color: '#6b7280' }}>Domain not found.</p>
				</div>
			);
		}

		return (
			<>
				<div style={{ ...cardStyle, padding: '14px 16px', display: 'grid', gap: '12px' }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
						<div>
							<h3 style={{ margin: 0, fontSize: '18px' }}>{domain.name || 'Domain'}</h3>
							<p style={{ margin: '6px 0 0', color: '#6b7280' }}>Domain details.</p>
						</div>
						<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
							<button type="button" onClick={() => navigate(`/super-admin/domains/${domain._id}/users`)} style={{ border: '1px solid', background: 'transparent' }}>
								View Users ({domain.userCount || 0})
							</button>
							<button type="button" onClick={() => navigate('/super-admin/domains')} style={{ border: '1px solid', background: 'transparent' }}>
								Back to Domains
							</button>
						</div>
					</div>

					{editingDomain ? (
						<div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
							<div>
								<label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Name</label>
								<input type="text" style={{ width: '100%' }} value={domainForm.name} onChange={(e) => onDomainFormChange('name', e.target.value)} />
							</div>
							<div>
								<label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Heading</label>
								<input type="text" style={{ width: '100%' }} value={domainForm.heading} onChange={(e) => onDomainFormChange('heading', e.target.value)} />
							</div>
							<div>
								<label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Admin Email</label>
								<input type="email" style={{ width: '100%' }} value={domainForm.adminEmail} onChange={(e) => onDomainFormChange('adminEmail', e.target.value)} />
							</div>
							<div>
								<label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>From Email</label>
								<input type="email" style={{ width: '100%' }} value={domainForm.fromEmail} onChange={(e) => onDomainFormChange('fromEmail', e.target.value)} />
							</div>
							<div>
								<label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Base URL</label>
								<input type="text" style={{ width: '100%' }} value={domainForm.base} onChange={(e) => onDomainFormChange('base', e.target.value)} placeholder="https://example.surecodesoftware.com" />
							</div>
						</div>
					) : (
						<div style={{ display: 'grid', gap: '10px', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
							<div><strong>Name:</strong> {domain.name || '-'}</div>
							<div><strong>Heading:</strong> {domain.heading || '-'}</div>
							<div><strong>Business Type:</strong> {domain.businessType || '-'}</div>
							<div><strong>Admin Email:</strong> {domain.adminEmail || '-'}</div>
							<div><strong>From Email:</strong> {domain.fromEmail || '-'}</div>
							<div><strong>Base URL:</strong> {domain.base || '-'}</div>
							<div><strong>Status:</strong> <span style={{ color: domain.disabled ? '#b91c1c' : '#15803d', fontWeight: 600 }}>{domain.disabled ? 'Disabled' : 'Active'}</span></div>
							<div><strong>Paid Extras:</strong> <span style={{ color: domain.paidExtrasAvailable ? '#15803d' : '#6b7280', fontWeight: 600 }}>{domain.paidExtrasAvailable ? 'Available' : 'Not available'}</span></div>
							<div><strong>Created:</strong> {formatDate(domain.createdAt)}</div>
							<div><strong>Updated:</strong> {formatDate(domain.updatedAt)}</div>
							<div><strong>Hostname:</strong> {domain.hostname || '-'}</div>
						</div>
					)}
				</div>

				<div style={{ ...cardStyle, padding: '14px 16px' }}>
					<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
						{editingDomain ? (
							<>
								<button type="button" onClick={saveDomain} disabled={savingDomain} className="btn btnPrimary">
									{savingDomain ? 'Saving...' : 'Save Changes'}
								</button>
								<button type="button" onClick={() => setEditingDomain(false)} disabled={savingDomain} style={{ border: '1px solid', background: 'transparent' }}>
									Cancel
								</button>
							</>
						) : (
							<>
								<button type="button" onClick={() => setEditingDomain(true)} style={{ border: '1px solid', background: 'transparent' }}>
									Edit
								</button>
								<button
									type="button"
									onClick={() => toggleDomainDisabled(domain)}
									disabled={togglingDomainId === domain._id}
									style={{
										border: `1px solid ${domain.disabled ? '#15803d' : '#b91c1c'}`,
										color: domain.disabled ? '#15803d' : '#b91c1c',
										background: 'transparent'
									}}
								>
									{togglingDomainId === domain._id ? '...' : domain.disabled ? 'Enable' : 'Disable'}
								</button>
								{domain.businessType === 'sessions' && (
									<button
										type="button"
										onClick={() => togglePaidExtrasAvailable(domain)}
										disabled={togglingDomainId === domain._id}
										style={{
											border: `1px solid ${domain.paidExtrasAvailable ? '#b91c1c' : '#15803d'}`,
											color: domain.paidExtrasAvailable ? '#b91c1c' : '#15803d',
											background: 'transparent'
										}}
									>
										{togglingDomainId === domain._id ? '...' : domain.paidExtrasAvailable ? 'Revoke Paid Extras' : 'Make Paid Extras Available'}
									</button>
								)}
								{!domain.disabled && domain.directUrl ? (
									<a href={domain.directUrl} target="_blank" rel="noreferrer" className="btn btnPrimary" style={{ textDecoration: 'none' }}>
										Login
									</a>
								) : null}
							</>
						)}
					</div>
				</div>
			</>
		);
	};

	const renderDomainUsers = () => (
		<>
			<div style={{ ...cardStyle, padding: '14px 16px', display: 'grid', gap: '10px' }}>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
					<div>
						<h3 style={{ margin: 0, fontSize: '18px' }}>{domainDetail?.name || 'Domain'} Users</h3>
						<p style={{ margin: '6px 0 0', color: '#6b7280' }}>Users for the selected domain.</p>
					</div>
					<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
						<button
							type="button"
							className="btn btnPrimary"
							onClick={() => {
								setAddAdminResult(null);
								setAddAdminError(null);
								setShowAddAdmin((prev) => !prev);
							}}
						>
							{showAddAdmin ? 'Cancel' : 'Add Admin'}
						</button>
						<button type="button" className="btn" onClick={() => navigate('/super-admin/domains')}>
							Back to Domains
						</button>
					</div>
				</div>
				<input
					type="text"
					placeholder="Search users..."
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					style={{ width: '100%' }}
				/>
			</div>

			{showAddAdmin ? (
				<div style={{ ...cardStyle, padding: '14px 16px', display: 'grid', gap: '12px' }}>
					<h4 style={{ margin: 0, fontSize: '15px' }}>Add Admin to {domainDetail?.name || 'this domain'}</h4>

					{addAdminResult ? (
						<div style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: '6px', padding: '12px', fontSize: '14px' }}>
							<p style={{ margin: 0 }}>
								Admin <strong>{addAdminResult.email}</strong> was created successfully.
							</p>
							<p style={{ margin: '6px 0 0' }}>
								Temporary password: <strong>{addAdminResult.temporaryPassword}</strong> (share this with the admin so they can log in and change it).
							</p>
						</div>
					) : (
						<>
							<div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
								<div>
									<label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>First Name</label>
									<input type="text" style={{ width: '100%' }} value={addAdminForm.firstName} onChange={(e) => onAddAdminFormChange('firstName', e.target.value)} />
								</div>
								<div>
									<label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Last Name</label>
									<input type="text" style={{ width: '100%' }} value={addAdminForm.lastName} onChange={(e) => onAddAdminFormChange('lastName', e.target.value)} />
								</div>
								<div>
									<label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Email</label>
									<input type="email" style={{ width: '100%' }} value={addAdminForm.email} onChange={(e) => onAddAdminFormChange('email', e.target.value)} />
								</div>
								<div>
									<label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Phone Number (optional)</label>
									<input type="text" style={{ width: '100%' }} value={addAdminForm.phoneNumber} onChange={(e) => onAddAdminFormChange('phoneNumber', e.target.value)} />
								</div>
							</div>

							{addAdminError ? (
								<div style={{ background: '#fde8e8', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '6px', padding: '10px' }}>
									{addAdminError}
								</div>
							) : null}

							<div>
								<button type="button" className="btn btnPrimary" onClick={addDomainAdmin} disabled={addingAdmin}>
									{addingAdmin ? 'Adding...' : 'Add Admin'}
								</button>
							</div>
						</>
					)}
				</div>
			) : null}

			<div style={{ ...cardStyle, overflow: 'hidden' }}>
				<div style={{ overflowX: 'auto' }}>
					<table style={{ width: '100%', borderCollapse: 'collapse' }}>
						<thead>
							<tr style={{ background: '#f8fafc' }}>
								<th style={thStyle}>Name</th>
								<th style={thStyle}>Email</th>
								<th style={thStyle}>Role</th>
								<th style={thStyle}>Status</th>
							</tr>
						</thead>
						<tbody>
							{filteredUsers.map((user) => (
								<tr key={user._id} style={{ borderTop: '1px solid #eef0f2' }}>
									<td style={tdStyle}>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || '-'}</td>
									<td style={tdStyle}>{user.email}</td>
									<td style={tdStyle}>{user.admin ? 'Domain Admin' : 'User'}</td>
									<td style={tdStyle}>{user.isActive === false ? 'Inactive' : 'Active'}</td>
								</tr>
							))}
							{!filteredUsers.length ? (
								<tr>
									<td colSpan={4} style={{ ...tdStyle, color: '#6b7280' }}>
										No users found for this domain.
									</td>
								</tr>
							) : null}
						</tbody>
					</table>
				</div>
			</div>
		</>
	);

	const pageTitle = page === 'dashboard'
		? 'Dashboard'
		: page === 'customers'
			? 'Customers'
			: page === 'customer-view'
				? 'Customer'
			: page === 'domain-view'
				? 'Domain'
			: page === 'domain-users'
				? 'Domain Users'
				: 'Domains';

	return (
		<div style={{ minHeight: '100vh', background: '#e5e7eb', display: 'flex' }}>
			{renderSidebar()}

			<main style={{ flex: 1, padding: '24px' }}>
				<div style={{ maxWidth: '1300px', margin: '0 auto', display: 'grid', gap: '16px' }}>
					<div style={{ ...cardStyle, padding: '16px 20px' }}>
						<h1 style={{ margin: 0, fontSize: '24px' }}>{pageTitle}</h1>
					</div>

					{loading ? (
						<div style={{ ...cardStyle, padding: '18px 20px' }}>
							<p style={{ margin: 0 }}>Loading...</p>
						</div>
					) : null}

					{error ? (
						<div style={{ background: '#fde8e8', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '6px', padding: '12px' }}>
							{error}
						</div>
					) : null}

					{!loading && !error && page === 'dashboard' ? renderDashboard() : null}
					{!loading && !error && page === 'customers' ? renderCustomers() : null}
					{!loading && !error && page === 'customer-view' ? renderCustomerView() : null}
					{!loading && !error && page === 'domains' ? renderDomains() : null}
					{!loading && !error && page === 'domain-view' ? renderDomainView() : null}
					{!loading && !error && page === 'domain-users' ? renderDomainUsers() : null}
				</div>
			</main>
		</div>
	);
}

export default SuperAdminPortal;
